import React, { useState } from 'react';
import { Switch, Route, useHistory } from 'react-router-dom';
import {
  Box,
  Grid,
  Stack,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import AcUnitOutlinedIcon from '@mui/icons-material/AcUnitOutlined';
import TrendingDownOutlinedIcon from '@mui/icons-material/TrendingDownOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { PageHeader, StatCard, LoadingState, DetailCard } from '../components';
import {
  useInventoryValuation,
  useStockMovement,
  useLowStockReport,
  useExpiryForecast,
  useDeadStock,
  useShrinkage,
} from '../hooks/useReports';
import { colors, formatCurrency, formatDate, humanize } from '../constants';

const BASE = '/inventory-management/reports';

const REPORTS = [
  { title: 'Inventory Valuation', description: 'Total value and per-product breakdown', icon: <PaidOutlinedIcon />, path: `${BASE}/valuation`, color: colors.success },
  { title: 'Stock Movement', description: 'Transaction summary by type and source', icon: <TrendingUpOutlinedIcon />, path: `${BASE}/stock-movement`, color: colors.primary },
  { title: 'Low Stock', description: 'Items below minimum threshold', icon: <WarningAmberOutlinedIcon />, path: `${BASE}/low-stock`, color: colors.warning },
  { title: 'Expiry Forecast', description: 'Products expiring soon', icon: <AccessTimeOutlinedIcon />, path: `${BASE}/expiry-forecast`, color: colors.error },
  { title: 'Dead Stock', description: 'Slow-moving inventory', icon: <AcUnitOutlinedIcon />, path: `${BASE}/dead-stock`, color: '#8e24aa' },
  { title: 'Shrinkage', description: 'Inventory loss analysis', icon: <TrendingDownOutlinedIcon />, path: `${BASE}/shrinkage`, color: '#d81b60' },
];

function ReportsMenu() {
  const history = useHistory();
  return (
    <>
      <PageHeader title="Reports" />
      <Grid container spacing={2}>
        {REPORTS.map((r) => (
          <Grid item xs={12} sm={6} md={4} key={r.title}>
            <Paper
              elevation={0}
              onClick={() => history.push(r.path)}
              sx={{
                p: 2.5, borderRadius: 3, border: `1px solid ${colors.border}`, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 2, transition: 'box-shadow .2s',
                '&:hover': { boxShadow: 3 },
              }}
            >
              <Box sx={{ width: 48, height: 48, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: `${r.color}1a`, color: r.color, flexShrink: 0 }}>
                {r.icon}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.navy }}>{r.title}</Typography>
                <Typography variant="body2" sx={{ color: colors.gray500 }}>{r.description}</Typography>
              </Box>
              <ChevronRightIcon sx={{ color: colors.gray400 }} />
            </Paper>
          </Grid>
        ))}
      </Grid>
    </>
  );
}

function ValuationReport() {
  const { data, isLoading } = useInventoryValuation();
  if (isLoading) return <LoadingState />;
  return (
    <>
      <PageHeader title="Inventory Valuation" backTo={BASE} />
      {data && (
        <Stack spacing={2.5}>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}><StatCard label="Total Cost" value={formatCurrency(data.totalInventoryCost || 0)} icon={<PaidOutlinedIcon />} color={colors.success} /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Total Qty" value={data.totalQuantity} icon={<TrendingUpOutlinedIcon />} color={colors.primary} /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Unique Products" value={data.uniqueProducts} color="#8e24aa" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Costing Method" value={data.costingMethod?.toUpperCase()} color={colors.gray600} /></Grid>
          </Grid>
          <DetailCard title="By Product">
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>SKU</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Avg Cost</TableCell>
                    <TableCell align="right">Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.items || []).map((item) => (
                    <TableRow key={item.productUuid}>
                      <TableCell>{item.sku}</TableCell>
                      <TableCell align="right">{item.totalQuantity}</TableCell>
                      <TableCell align="right">{formatCurrency(item.averageCost || 0)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(item.totalValue || 0)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </DetailCard>
        </Stack>
      )}
    </>
  );
}

function DateRange({ from, to, setFrom, setTo }) {
  return (
    <Stack direction="row" spacing={2} sx={{ mb: 2.5 }}>
      <TextField label="From" type="date" size="small" InputLabelProps={{ shrink: true }} value={from} onChange={(e) => setFrom(e.target.value)} />
      <TextField label="To" type="date" size="small" InputLabelProps={{ shrink: true }} value={to} onChange={(e) => setTo(e.target.value)} />
    </Stack>
  );
}

function StockMovementReport() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const { data, isLoading } = useStockMovement(from || to ? { from: from || undefined, to: to || undefined } : undefined);
  return (
    <>
      <PageHeader title="Stock Movement" backTo={BASE} />
      <DateRange from={from} to={to} setFrom={setFrom} setTo={setTo} />
      {isLoading && !data ? (
        <LoadingState />
      ) : data ? (
        <Stack spacing={2.5}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}><StatCard label="Total Transactions" value={data.totalTransactions} icon={<TrendingUpOutlinedIcon />} color={colors.primary} /></Grid>
          </Grid>
          <DetailCard title="By Type">
            {Object.entries(data.byType || {}).map(([type, val]) => (
              <Stack key={type} direction="row" justifyContent="space-between" sx={{ py: 1, borderBottom: `1px solid ${colors.gray100}` }}>
                <Typography sx={{ textTransform: 'capitalize' }}>{type.replace(/_/g, ' ')}</Typography>
                <Stack direction="row" spacing={2}>
                  <Typography sx={{ color: colors.gray500 }}>{val.count} txns</Typography>
                  <Typography sx={{ fontWeight: 700 }}>{val.totalQuantity} units</Typography>
                </Stack>
              </Stack>
            ))}
          </DetailCard>
          <DetailCard title="By Source">
            {Object.entries(data.bySource || {}).map(([source, val]) => (
              <Stack key={source} direction="row" justifyContent="space-between" sx={{ py: 1, borderBottom: `1px solid ${colors.gray100}` }}>
                <Typography sx={{ textTransform: 'capitalize' }}>{source.replace(/_/g, ' ')}</Typography>
                <Stack direction="row" spacing={2}>
                  <Typography sx={{ color: colors.gray500 }}>{val.count} txns</Typography>
                  <Typography sx={{ fontWeight: 700 }}>{val.totalQuantity} units</Typography>
                </Stack>
              </Stack>
            ))}
          </DetailCard>
        </Stack>
      ) : null}
    </>
  );
}

function LowStockReport() {
  const { data, isLoading } = useLowStockReport();
  if (isLoading) return <LoadingState />;
  return (
    <>
      <PageHeader title="Low Stock Report" backTo={BASE} />
      {data && (
        <Stack spacing={2.5}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}><StatCard label="Low Stock Items" value={data.totalLowStockItems} icon={<WarningAmberOutlinedIcon />} color={colors.warning} /></Grid>
          </Grid>
          <DetailCard>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell align="right">Current</TableCell>
                    <TableCell align="right">Min</TableCell>
                    <TableCell align="right">Shortfall</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.items || []).map((item) => (
                    <TableRow key={item.productUuid}>
                      <TableCell>
                        <Typography variant="body2">{item.name}</Typography>
                        <Typography variant="caption" sx={{ color: colors.gray400 }}>{item.sku}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ color: colors.error }}>{item.currentStock}</TableCell>
                      <TableCell align="right">{item.minStockLevel}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: colors.error }}>{item.shortfall}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </DetailCard>
        </Stack>
      )}
    </>
  );
}

function ExpiryBucket({ title, bucket, color }) {
  if (!bucket || bucket.count === 0) return null;
  return (
    <DetailCard
      title={<span style={{ color }}>{title}</span>}
      action={<Typography variant="body2" sx={{ color: colors.gray500 }}>{bucket.count} lots | {formatCurrency(bucket.totalValue || 0)}</Typography>}
    >
      {(bucket.items || []).map((item) => (
        <Stack key={item.lotUuid} direction="row" justifyContent="space-between" sx={{ py: 1, borderBottom: `1px solid ${colors.gray100}` }}>
          <Box>
            <Typography variant="body2">{item.sku}</Typography>
            <Typography variant="caption" sx={{ color: colors.gray400 }}>{item.lotNumber}</Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2">{item.currentQuantity} units</Typography>
            <Typography variant="caption" sx={{ color }}>{item.expiryDate ? formatDate(item.expiryDate) : '—'}</Typography>
          </Box>
        </Stack>
      ))}
    </DetailCard>
  );
}

function ExpiryForecastReport() {
  const { data, isLoading } = useExpiryForecast();
  if (isLoading) return <LoadingState />;
  return (
    <>
      <PageHeader title="Expiry Forecast" backTo={BASE} />
      {data && (
        <Stack spacing={2.5}>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}><StatCard label="Expired" value={data.expired?.count || 0} color={colors.error} /></Grid>
            <Grid item xs={6} md={3}><StatCard label="7 Days" value={data.expiringIn7Days?.count || 0} color={colors.warning} /></Grid>
            <Grid item xs={6} md={3}><StatCard label="30 Days" value={data.expiringIn30Days?.count || 0} color="#fb8c00" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="90 Days" value={data.expiringIn90Days?.count || 0} color={colors.info} /></Grid>
          </Grid>
          <ExpiryBucket title="Expired" bucket={data.expired} color={colors.error} />
          <ExpiryBucket title="Expiring in 7 Days" bucket={data.expiringIn7Days} color={colors.warning} />
          <ExpiryBucket title="Expiring in 30 Days" bucket={data.expiringIn30Days} color={colors.warning} />
          <ExpiryBucket title="Expiring in 90 Days" bucket={data.expiringIn90Days} color={colors.primary} />
        </Stack>
      )}
    </>
  );
}

function DeadStockReport() {
  const { data, isLoading } = useDeadStock();
  if (isLoading) return <LoadingState />;
  return (
    <>
      <PageHeader title="Dead Stock" backTo={BASE} />
      {data && (
        <Stack spacing={2.5}>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}><StatCard label="Dead Items" value={data.totalDeadStockItems} icon={<AcUnitOutlinedIcon />} color="#8e24aa" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Total Value" value={formatCurrency(data.totalValue || 0)} icon={<PaidOutlinedIcon />} color={colors.error} /></Grid>
          </Grid>
          <DetailCard title={`Threshold: ${data.thresholdDays} days`}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Days</TableCell>
                    <TableCell align="right">Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.items || []).map((item) => (
                    <TableRow key={item.lotUuid}>
                      <TableCell>
                        <Typography variant="body2">{item.sku}</Typography>
                        <Typography variant="caption" sx={{ color: colors.gray400 }}>{item.lotNumber}</Typography>
                      </TableCell>
                      <TableCell align="right">{item.currentQuantity}</TableCell>
                      <TableCell align="right" sx={{ color: '#8e24aa' }}>{item.daysInStock}</TableCell>
                      <TableCell align="right">{formatCurrency(item.totalValue || 0)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </DetailCard>
        </Stack>
      )}
    </>
  );
}

function ShrinkageReport() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const { data, isLoading } = useShrinkage(from || to ? { from: from || undefined, to: to || undefined } : undefined);
  return (
    <>
      <PageHeader title="Shrinkage Report" backTo={BASE} />
      <DateRange from={from} to={to} setFrom={setFrom} setTo={setTo} />
      {isLoading && !data ? (
        <LoadingState />
      ) : data ? (
        <Stack spacing={2.5}>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}><StatCard label="Events" value={data.totalShrinkageEvents} icon={<TrendingDownOutlinedIcon />} color="#d81b60" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Qty Lost" value={data.totalQuantityLost} color={colors.error} /></Grid>
          </Grid>
          <DetailCard title="By Reason">
            {Object.entries(data.byReason || {}).map(([reason, val]) => (
              <Stack key={reason} direction="row" justifyContent="space-between" sx={{ py: 1, borderBottom: `1px solid ${colors.gray100}` }}>
                <Typography sx={{ textTransform: 'capitalize' }}>{humanize(reason)}</Typography>
                <Stack direction="row" spacing={2}>
                  <Typography sx={{ color: colors.gray500 }}>{val.count} events</Typography>
                  <Typography sx={{ fontWeight: 700, color: colors.error }}>{val.totalQuantity} lost</Typography>
                </Stack>
              </Stack>
            ))}
          </DetailCard>
        </Stack>
      ) : null}
    </>
  );
}

export default function Reports() {
  return (
    <Switch>
      <Route exact path={BASE} component={ReportsMenu} />
      <Route exact path={`${BASE}/valuation`} component={ValuationReport} />
      <Route exact path={`${BASE}/stock-movement`} component={StockMovementReport} />
      <Route exact path={`${BASE}/low-stock`} component={LowStockReport} />
      <Route exact path={`${BASE}/expiry-forecast`} component={ExpiryForecastReport} />
      <Route exact path={`${BASE}/dead-stock`} component={DeadStockReport} />
      <Route exact path={`${BASE}/shrinkage`} component={ShrinkageReport} />
    </Switch>
  );
}
